import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import AnnualReviewService from '../../../../../services/AnnualReview';

interface AnnualReviewItem {
  id?: string;
  cycleYear?: number;
  status?: string;
  finalSignedAt?: string | null;
  supervisorCompletedAt?: string | null;
  employeeCompletedAt?: string | null;
  createdAt?: string;
  finalSignedPdfUrl?: string | null;
  draftCombinedPdfUrl?: string | null;
}

interface AnnualReviewSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
}

const formatStatus = (status?: string): string =>
  (status || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AnnualReviewSummaryModal = ({ isOpen, toggle, userId, userName }: AnnualReviewSummaryModalProps) => {
  const { token }: any = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AnnualReviewItem[]>([]);

  const load = useCallback(async () => {
    if (!isOpen || !userId || !token) return;
    setLoading(true);
    try {
      const res = await AnnualReviewService(token).listByUserId(userId);
      const list = Array.isArray(res) ? res : res?.data || [];
      setItems(list);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not load annual reviews');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, userId, token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Annual Reviews{userName ? ` — ${userName}` : ''}</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-muted mb-0">No annual reviews to display.</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-items-center">
              <thead className="thead-light">
                <tr>
                  <th scope="col">Cycle</th>
                  <th scope="col">Status</th>
                  <th scope="col">Completed</th>
                  <th scope="col" className="text-center">
                    Document
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const pdfUrl = item.finalSignedPdfUrl || item.draftCombinedPdfUrl || null;
                  const completedAt = item.finalSignedAt || item.supervisorCompletedAt || item.createdAt;
                  return (
                    <tr key={item.id || index}>
                      <td>{item.cycleYear || '-'}</td>
                      <td>{formatStatus(item.status)}</td>
                      <td>{completedAt ? moment(completedAt).format('MM/DD/YYYY') : '-'}</td>
                      <td className="text-center">
                        {pdfUrl ? (
                          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AnnualReviewSummaryModal;
